import { SEARCH_IDLE_RESET_MS } from '@mixwire/contracts';
import { useState, type ReactElement } from 'react';

import { readApiFailure } from '../api/client.js';
import { AccentCard } from '../components/AccentCard.js';
import { Logo } from '../components/Logo.js';
import { SyncMeter } from '../components/SyncMeter.js';
import { TrackSearch } from '../components/TrackSearch.js';
import { useDebouncedValue } from '../hooks/useDebouncedValue.js';
import { useIdleReset } from '../hooks/useIdleReset.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { useSuggesterSearch } from '../hooks/useSuggesterSearch.js';
import { useSuggesterView } from '../hooks/useSuggesterView.js';
import { useSuggestTrack } from '../hooks/useSuggestTrack.js';
import { partyPath } from '../routing/paths.js';
import { useRoute } from '../routing/useRoute.js';

import type {
  ChannelId,
  PartySlug,
  QueuePreviewItem,
  SearchResultTrack,
  SuggesterToken,
} from '@mixwire/contracts';

// Long enough that typing a few words is one search, short enough the list
// still feels like it's keeping up.
const SEARCH_DEBOUNCE_MS = 300;

// Client-side courtesy only, matching the server's own throttle window
// exactly so the button never re-enables into a rejection.
const UPVOTE_THROTTLE_MS = 10_000;

const HowItWorks = (): ReactElement => (
  <div className="text-secondary flex flex-col gap-2.5 px-4 py-8 text-sm leading-relaxed">
    <p>Search the whole Spotify catalogue, not just what is already queued.</p>
    <p>Tap Suggest and the track goes to every DJ at once.</p>
    <p>
      Suggest one somebody has already sent and it counts as an upvote, which bumps it up the DJs’
      list.
    </p>
    <p>No DJ has to play it.</p>
  </div>
);

const PreviewCard = ({ item }: { readonly item: QueuePreviewItem }): ReactElement => (
  <AccentCard colour={item.channelColour} className="p-2.5">
    <div className="flex items-baseline justify-between gap-2">
      <p className="text-content truncate text-sm font-medium">{item.title}</p>
      <span className="text-muted shrink-0 text-xs tabular-nums">{item.eta}</span>
    </div>
    <p className="text-muted truncate text-xs">{item.artist}</p>
  </AccentCard>
);

type SuggesterPageProps = {
  readonly slug: PartySlug;
  readonly token: SuggesterToken;
};

export const SuggesterPage = ({ slug, token }: SuggesterPageProps): ReactElement => {
  const [query, setQuery] = useState('');
  const [channelFilter, setChannelFilter] = useState<ChannelId | undefined>(undefined);
  const [throttledIds, setThrottledIds] = useState<ReadonlySet<string>>(new Set());
  const view = useSuggesterView(slug, token);
  // The input stays instant; only the searching waits for the typing to settle.
  const term = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const results = useSuggesterSearch(slug, token, term);
  const suggest = useSuggestTrack();
  const { navigate } = useRoute();

  usePageTitle(view.data?.partyDisplayName);

  const noteSearchActivity = useIdleReset({
    isArmed: query !== '',
    resetAfterMs: SEARCH_IDLE_RESET_MS,
    onReset: () => {
      setQuery('');
    },
  });

  const handleSuggest = (track: SearchResultTrack): void => {
    noteSearchActivity();
    setThrottledIds((ids) => new Set([...ids, track.id]));
    suggest.mutate({ slug, token, track: track.id });
    setTimeout(() => {
      setThrottledIds((ids) => new Set([...ids].filter((id) => id !== track.id)));
    }, UPVOTE_THROTTLE_MS);
  };

  const failure = readApiFailure(suggest.error) ?? readApiFailure(results.error);
  // True only while there's nothing to show for this term; a background
  // refetch leaves current results in place.
  const isSearching = term !== '' && results.data === undefined && results.isFetching;
  const preview = (view.data?.preview ?? []).filter(
    (item) => channelFilter === undefined || item.channelId === channelFilter,
  );

  return (
    // Below `lg` the queue preview sits under the search, not beside it: a
    // phone is the normal screen, and a 20rem sidebar leaves neither panel readable.
    <div className="bg-surface rounded-card border-subtle flex flex-col overflow-hidden border lg:flex-row">
      <div className="border-subtle flex flex-1 flex-col border-b lg:border-r lg:border-b-0">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 px-4 py-3.5 sm:px-6 sm:py-4">
          <div className="flex min-w-0 items-center gap-2">
            <Logo className="text-content size-6" />
            <span className="font-display text-content text-xl">MixWire</span>
            <span className="bg-line mx-1 h-5 w-px" />
            <span className="text-muted text-xs tracking-widest uppercase">Privileged device</span>
          </div>
          <div className="flex min-w-0 items-center gap-2.5">
            {view.data === undefined ? null : (
              <span className="text-secondary truncate text-sm">{view.data.partyDisplayName}</span>
            )}
            <SyncMeter />
          </div>
        </div>

        {/* No "sent" banner: the row itself shows the upvote count instead, without shifting every row below it down the page. */}
        <TrackSearch
          query={query}
          onQueryChange={setQuery}
          term={term}
          results={results.data?.items ?? []}
          isSearching={isSearching}
          failureMessage={failure?.message}
          inputLabel="Search Spotify for a track"
          emptyQueryContent={<HowItWorks />}
          actionLabel={(track) => (track.suggestedCount === 0 ? 'Suggest' : 'Upvote +1')}
          isActionDisabled={(track) =>
            (suggest.isPending && suggest.variables.track === track.id) ||
            throttledIds.has(track.id)
          }
          onAction={handleSuggest}
          onActivity={noteSearchActivity}
        />

        <div className="border-subtle bg-surface-sunken flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t px-4 py-3.5 sm:px-6">
          <span className="text-secondary text-sm">Suggest as many as you like.</span>
          <span className="text-muted text-xs tracking-widest uppercase">
            Goes to every DJ at once
          </span>
        </div>
      </div>

      {/* Stacked, a sunken panel would match the canvas colour and look like the card ended at the search results; it only reads as recessed beside the column, at `lg`. */}
      <div className="bg-surface flex w-full shrink-0 flex-col p-4 sm:p-5 lg:bg-surface-sunken lg:w-80 lg:p-5">
        <p className="text-muted text-xs tracking-widest uppercase">Queue preview</p>

        <div className="mt-3 flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => {
              setChannelFilter(undefined);
            }}
            className={`rounded-pill px-3 py-1.5 text-xs font-medium ${
              channelFilter === undefined
                ? 'bg-content text-inverted'
                : 'border-line text-secondary border'
            }`}
          >
            All channels
          </button>
          {(view.data?.chips ?? []).map((chip) => (
            <button
              key={chip.channelId}
              type="button"
              onClick={() => {
                setChannelFilter(chip.channelId);
              }}
              className="flex items-center gap-1.5 rounded-pill border px-3 py-1.5 text-xs font-medium"
              style={{
                borderColor: chip.colour,
                color: chip.colour,
                backgroundColor: channelFilter === chip.channelId ? `${chip.colour}1a` : undefined,
              }}
            >
              <span className="size-2 rounded-full" style={{ backgroundColor: chip.colour }} />
              {chip.label}
            </button>
          ))}
        </div>

        <ul className="mt-4 flex flex-1 list-none flex-col gap-2 overflow-y-auto p-0">
          {preview.map((item) => (
            <PreviewCard key={item.id} item={item} />
          ))}
        </ul>

        <div className="border-subtle mt-3 border-t pt-3.5">
          <p className="text-secondary text-xs leading-relaxed">
            You are a suggester, not a DJ. Anything you send goes to one shared list — whichever DJ
            likes it plays it.
          </p>
          <button
            type="button"
            onClick={() => {
              navigate(partyPath(slug));
            }}
            className="text-content decoration-accent hover:text-secondary mt-2.5 text-xs underline decoration-2 underline-offset-2"
          >
            Back to view only
          </button>
        </div>
      </div>
    </div>
  );
};
